<template>
  <v-card color="transparent">
    <v-card-text>
      <v-row>
        <v-col cols="12" lg="7" md="7" xl="6">
          <section-header v-if="$vuetify.breakpoint.smAndDown" class="mb-5">
            {{ $t('reports.input_map') }}
          </section-header>
          <div class="chart-wrapper">
            <div id="inputMapContainer"></div>
          </div>
        </v-col>
        <v-col class="text-body-2 text-sm-body-1" cols="12" lg="5" md="5" xl="6">
          <v-row align="center" class="fill-height" no-gutters>
            <div>
              <section-header v-if="$vuetify.breakpoint.mdAndUp" class="mb-5">
                {{ $t('reports.input_map') }}
              </section-header>
              <p>{{ $t('reports.input_map_explained') }}</p>
              <p>{{ $t('reports.input_map_help') }}</p>

              <SWOTLegends/>
            </div>
          </v-row>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import Vue from 'vue';
import Highcharts from 'highcharts';
import 'highcharts/highcharts-more';
import 'highcharts/modules/accessibility';
import categoryService from '@/service/category.service';
import SectionHeader from '@/components/SectionHeader';
import InputMapLabel from '@/components/Tenant/Reports/InputMapLabel';
import SWOTLegends from '@/components/Tenant/Reports/SWOTLegends';

export default {
  name: 'ReportInputMapCard',
  components: {SWOTLegends, SectionHeader},
  props: ['personnelSurvey', 'managementSurvey'],
  data() {
    return {
      chart: null,
      chartCategories: ['efficiency', 'capability'],
      componentInstances: [],
    };
  },
  beforeDestroy() {
    this.componentInstances.forEach(i => i.$destroy());
  },
  beforeRouteLeave() {
    this.componentInstances.forEach(i => i.$destroy());
  },
  methods: {
    labelFormatter(data, chart, combinedData) {
      const tenant = this.$tenant;
      const labelDataIndex = combinedData.findIndex(d => d.name === data.value);
      let labelData = data.value;
      if (labelDataIndex > -1) {
        labelData = combinedData[labelDataIndex].labelData;
      }
      const ComponentClass = Vue.extend(InputMapLabel);
      const instance = new ComponentClass({propsData: {pointData: data, chart, tenant, labelData}});
      const mountedInstance = instance.$mount();
      const html = mountedInstance.$el.innerHTML;
      this.componentInstances.push(mountedInstance);
      return html;
    },
    createChart() {
      const combinedData = this.combinedData;
      const that = this;
      const chart = Highcharts.chart('inputMapContainer', {
        chart: {
          polar: true,
          type: 'line',
          style: {
            fontFamily: 'Jost',
          },
          spacingBottom: 0,
          spacingTop: 10,
          spacingLeft: 10,
          spacingRight: 30,
          backgroundColor: 'transparent'
        },
        accessibility: {
          description: this.$t('reports.input_map_accessibility', {
            organizationPersonnel: combinedData.find(d => d.engineName === 'ORGANISAATIO')?.personnelValue,
            organizationManagement: combinedData.find(d => d.engineName === 'ORGANISAATIO')?.managementValue,
            processesPersonnel: combinedData.find(d => d.engineName === 'PROSESSIT')?.personnelValue,
            processesManagement: combinedData.find(d => d.engineName === 'PROSESSIT')?.managementValue,
            cooperationPersonnel: combinedData.find(d => d.engineName === 'YHTEYSTYÖ')?.personnelValue,
            cooperationManagement: combinedData.find(d => d.engineName === 'YHTEYSTYÖ')?.managementValue,
            motivationPersonnel: combinedData.find(d => d.engineName === 'MOTIVAATIO')?.personnelValue,
            motivationManagement: combinedData.find(d => d.engineName === 'MOTIVAATIO')?.managementValue,
            knowHowPersonnel: combinedData.find(d => d.engineName === 'OSAAMINEN')?.personnelValue,
            knowHowManagement: combinedData.find(d => d.engineName === 'OSAAMINEN')?.managementValue,
            workingAbilityPersonnel: combinedData.find(d => d.engineName === 'TYÖKYKY')?.personnelValue,
            workingAbilityManagement: combinedData.find(d => d.engineName === 'TYÖKYKY')?.managementValue,
            leadershipPersonnel: combinedData.find(d => d.engineName === 'leadership')?.personnelValue,
            leadershipManagement: combinedData.find(d => d.engineName === 'leadership')?.managementValue,
          }),
        },
        title: {
          text: '',
        },
        pane: {
          size: '85%',
        },
        xAxis: {
          categories: combinedData.map(d => d.name),
          tickmarkPlacement: 'on',
          lineWidth: 0,
          labels: {
            allowOverlap: true,
            useHTML: true,
            formatter: function (chart) {
              return that.labelFormatter(this, chart, combinedData);
            },
          },
        },
        yAxis: {
          gridLineInterpolation: 'polygon',
          lineWidth: 0,
          min: 0,
          max: 100,
          tickPositions: [0, 40, 60, 100],
          plotBands: [
            {from: 0, to: 40, color: this.$getScoreColorHex(0) + 'CC'},
            {from: 40, to: 60, color: this.$getScoreColorHex(50) + 'CC'},
            {from: 60, to: 100, color: this.$getScoreColorHex(100) + 'CC'},
          ],
        },
        legend: {
          align: 'center',
          verticalAlign: 'bottom',
          layout: 'horizontal',
        },
        /*tooltip: {
          formatter: function(chart) {
            return that.tooltipFormatter(this, chart);
          },
          useHTML: true,
        },*/
        tooltip: {
          shared: true,
          pointFormat: '<span style="color:{series.color}">{series.name}: <b>{point.y:,.0f}</b><br/>',
        },
        credits: false,
        series: [
          {
            name: this.$t('reports.management_input'),
            data: this.managementSeriesData,
            pointPlacement: 'on',
            color: '#F50057',
          },
          {
            name: this.$t('reports.personnel_input'),
            data: this.personnelSeriesData,
            pointPlacement: 'on',
            color: '#1A237E',
          },
        ],
        responsive: {
          rules: [
            {
              condition: {
                maxWidth: 500,
              },
              chartOptions: {
                pane: {
                  size: '72%',
                },
              },
            },
          ],
        },
      });

      this.chart = chart;
    },
  },
  watch: {
    personnelSeriesData(val) {
      const visible = val?.filter(value => value > -1).length > 0;
      this.chart.series[1].update({data: val, visible: visible}, true);
    },
    managementSeriesData(val) {
      const visible = val?.filter(value => value > -1).length > 0;
      this.chart.series[0].update({data: val, visible: visible}, true);
    },
  },
  computed: {
    categories() {
      const tree = {...categoryService.tree};
      return tree;
    },
    personnelSeriesData() {
      return this.combinedData.map(d => d.personnelValue);
    },
    managementSeriesData() {
      return this.combinedData.map(d => d.managementValue);
    },
    combinedData() {
      let combinedData = [];
      Object.keys(this.categories).forEach(category => {
        let subCategories = [];
        if (this.chartCategories.includes(category)) {
          subCategories = Object.values(this.categories[category]?.children).map(subCategory => {
            const subCategoryNameText = subCategory.nameText;
            const personnelSurveyData = this.personnelSurvey?.calculation?.engineResponse?.lta?.[category]?.avg?.filter(
              c => c.name === subCategory.engineName
            )[0].value;
            const managementSurveyData = this.managementSurvey?.calculation?.engineResponse?.oja?.[category]?.avg?.filter(
              c => c.name === subCategory.engineName
            )[0].value;
            return {
              name: `${subCategoryNameText} (${this.$t('reports.' + category)})`,
              engineName: subCategory.engineName,
              labelData: {
                name: `${subCategoryNameText} (${this.$t('reports.' + category)})`,
                categoryName: subCategoryNameText,
                engineName: subCategory.engineName,
                parent: category,
              },
              personnelValue: personnelSurveyData !== undefined ? personnelSurveyData : -1,
              managementValue: managementSurveyData !== undefined ? managementSurveyData : -1,
            };
          });
          combinedData = combinedData.concat(subCategories);
        }
      });

      let personnelLeadershipValue = -1;
      const personnelLeadershipArray = this.personnelSurvey?.calculation?.engineResponse?.lta?.['leadership']?.avg;
      if (personnelLeadershipArray) {
        personnelLeadershipArray.forEach(c => (personnelLeadershipValue += c.value));
        personnelLeadershipValue = personnelLeadershipValue / personnelLeadershipArray.length;
      }

      const managementLeadershipValue = this.managementSurvey?.calculation?.engineResponse?.oja?.['leadership']?.avg[0]?.value;

      combinedData.push({
        name: this.$t('reports.leadership'),
        engineName: 'leadership',
        labelData: {
          name: this.$t('reports.leadership'),
          categoryName: this.$t('reports.leadership'),
          engineName: 'leadership',
          parent: null,
        },
        personnelValue: personnelLeadershipValue,
        managementValue: managementLeadershipValue !== undefined ? managementLeadershipValue : -1,
      });

      return combinedData;
    },
  },
  mounted() {
    this.createChart();
  },
};
</script>

<style lang="scss">
/*
.highcharts-data-table table {
  min-width: 320px;
  max-width: 700px;
  margin: 1em auto;
}

.highcharts-data-table table {
  font-family: 'Fire Sans', sans-serif;
  border-collapse: collapse;
  border: 1px solid #000;
  margin: 10px auto;
  text-align: center;
  width: 100%;
  max-width: 500px;
}

.highcharts-data-table caption {
  padding: 1em 0;
  font-size: 1.2em;
  color: #555;
}

.highcharts-data-table th {
  font-weight: 600;
  padding: 0.5em;
}

.highcharts-data-table td,
.highcharts-data-table th,
.highcharts-data-table caption {
  padding: 0.5em;
}

.highcharts-data-table thead tr,
.highcharts-data-table tr:nth-child(even) {
  background: #f8f8f8;
}

.highcharts-data-table tr:hover {
  background: #f1f7ff;
}
*/

.highcharts-xaxis-grid .highcharts-grid-line {
  stroke-width: 2px;
  stroke: rgba(255, 255, 255, 1);
}

.highcharts-xaxis .highcharts-tick {
  stroke-width: 2px;
  stroke: #000;
}

.highcharts-yaxis-grid .highcharts-grid-line {
  stroke-width: 0;
}

.chart-wrapper,
#inputMapContainer {
  width: 100%;
}

@media screen and (max-width: 600px) {
  #inputMapContainer {
    //transform: scale(0.85);
  }
}
</style>
